import { useMemo, useState } from 'react';
import type { GetPermissionListResultDto } from '@/api/types/identity';

// Flat permission node with a computed depth, mirroring the legacy
// FlatTreeDepthFinder + the per-group card layout in Users/Roles Permissions.
interface FlatNode {
  name: string;
  displayName: string;
  parentName: string | null;
  depth: number;
}

interface FlatGroup {
  name: string;
  displayName: string;
  normalizedName: string;
  nodes: FlatNode[];
}

function computeDepth(name: string, byName: Map<string, string | null>): number {
  let depth = 0;
  let parent = byName.get(name) ?? null;
  const seen = new Set<string>();
  while (parent && byName.has(parent) && !seen.has(parent)) {
    seen.add(parent);
    depth += 1;
    parent = byName.get(parent) ?? null;
  }
  return depth;
}

// Returns the display DOM for one permission result, with the grant state held
// locally (checkbox map), parent/child cascade and a per-group "select all".
export function PermissionTree({
  result,
  grants,
  onToggle,
  disabled = false,
  filter: externalFilter,
}: {
  result: GetPermissionListResultDto;
  grants: Record<string, boolean>;
  onToggle: (next: Record<string, boolean>) => void;
  disabled?: boolean;
  // When provided, the filter is controlled by the caller and the built-in
  // filter input is hidden (e.g. the Roles page renders it in the card header).
  filter?: string;
}) {
  const [internalFilter, setInternalFilter] = useState('');
  const filter = externalFilter ?? internalFilter;
  const setFilter = setInternalFilter;

  const groups = useMemo<FlatGroup[]>(() => {
    return result.groups.map((g) => {
      const byName = new Map<string, string | null>();
      g.permissions.forEach((p) => byName.set(p.name, p.parentName ?? null));
      const isRedundantRoot = (displayName: string, parentName: string | null) =>
        (parentName === null || parentName === '') && displayName === g.displayName;
      const anyRedundantRoot = g.permissions.some((p) =>
        isRedundantRoot(p.displayName, p.parentName ?? null),
      );
      const depthOffset = anyRedundantRoot ? 0 : 1;
      const nodes: FlatNode[] = g.permissions
        .filter((p) => !isRedundantRoot(p.displayName, p.parentName ?? null))
        .map((p) => ({
          name: p.name,
          displayName: p.displayName,
          parentName: p.parentName ?? null,
          depth: computeDepth(p.name, byName) + depthOffset,
        }));
      return {
        name: g.name,
        displayName: g.displayName,
        normalizedName: g.name.replace(/\./g, '_'),
        nodes,
      };
    });
  }, [result]);

  function childrenOf(groupNodes: FlatNode[], parentName: string): FlatNode[] {
    return groupNodes.filter((n) => n.parentName === parentName);
  }

  function cascadeDescendants(
    groupNodes: FlatNode[],
    parentName: string,
    checked: boolean,
    next: Record<string, boolean>,
  ) {
    childrenOf(groupNodes, parentName).forEach((child) => {
      next[child.name] = checked;
      cascadeDescendants(groupNodes, child.name, checked, next);
    });
  }

  function toggleNode(group: FlatGroup, node: FlatNode, checked: boolean) {
    const next = { ...grants, [node.name]: checked };
    if (checked) {
      // Check all ancestors.
      let parent = node.parentName;
      const byName = new Map(group.nodes.map((n) => [n.name, n.parentName]));
      const seen = new Set<string>();
      while (parent && !seen.has(parent)) {
        seen.add(parent);
        next[parent] = true;
        parent = byName.get(parent) ?? null;
      }
    }
    cascadeDescendants(group.nodes, node.name, checked, next);
    onToggle(next);
  }

  function toggleAll(group: FlatGroup, checked: boolean) {
    const next = { ...grants };
    group.nodes.forEach((n) => {
      next[n.name] = checked;
    });
    onToggle(next);
  }

  const query = filter.toLowerCase().trim();

  if (groups.length === 0) {
    return <div className="my-2">No permissions defined.</div>;
  }

  return (
    <div>
      {externalFilter === undefined && (
        <div className="mb-3">
          <div className="input-group" style={{ maxWidth: 360 }}>
            <span className="input-group-text">
              <i className="fa fa-search" />
            </span>
            <input
              type="text"
              className="form-control"
              placeholder="Filter permissions..."
              value={filter}
              onChange={(e) => setFilter(e.target.value)}
            />
          </div>
        </div>
      )}

      <div className="d-flex flex-wrap">
        {groups.map((group) => {
          const allChecked =
            group.nodes.length > 0 && group.nodes.every((n) => grants[n.name]);
          return (
            <div key={group.name} className="col-lg-4">
              <div className="h-100 border mb-3 px-4 py-2">
                <div className="form-check fw-semibold">
                  <input
                    type="checkbox"
                    className="form-check-input"
                    id={`AllInGroup_${group.normalizedName}`}
                    checked={allChecked}
                    disabled={disabled}
                    onChange={(e) => toggleAll(group, e.target.checked)}
                  />
                  <label className="form-check-label" htmlFor={`AllInGroup_${group.normalizedName}`}>
                    {group.displayName}
                  </label>
                </div>

                <div className="pt-1">
                  {group.nodes.map((node) => {
                    const hidden =
                      query !== '' && !node.displayName.toLowerCase().includes(query);
                    return (
                      <div
                        key={node.name}
                        className="form-check"
                        style={{
                          marginInlineStart: node.depth * 20,
                          display: hidden ? 'none' : undefined,
                        }}
                      >
                        <input
                          type="checkbox"
                          className="form-check-input"
                          id={`perm_${node.name}`}
                          checked={grants[node.name] ?? false}
                          disabled={disabled}
                          onChange={(e) => toggleNode(group, node, e.target.checked)}
                        />
                        <label className="form-check-label" htmlFor={`perm_${node.name}`}>
                          {node.displayName}
                        </label>
                      </div>
                    );
                  })}
                </div>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}

// Flattens a permission result into the initial grant map the tree edits.
export function initialGrants(result: GetPermissionListResultDto): Record<string, boolean> {
  const map: Record<string, boolean> = {};
  result.groups.forEach((g) => g.permissions.forEach((p) => (map[p.name] = p.isGranted)));
  return map;
}
